import {
  type ReactNode,
  type Ref,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { Button, Flex, Input, message } from "antd"
import {
  getUploadableLocalFiles,
  type LocalUploadFile,
  uploadLocalResources,
} from "~/common/media/resourceUpload"
import { ApiRequestError } from "~/common/request"
import { useVideoUpload } from "~/common/media/useVideoUpload"
import {
  IMAGE_UPLOAD_REQUIREMENTS,
  getUploadedResourceKind,
  isValidUploadedVideoResource,
  getVideoUploadRequirements,
  type UploadedResource,
  uploadResourceLimits,
  VIDEO_UPLOAD_REQUIREMENTS,
} from "~/common/media/uploadedResources"
import { formatFileSize, isAbortError, linesToList } from "~/common/utils"
import { UploadIcon } from "~/components/Icons"
import { Uploader } from "~/components/Uploader"
import {
  defaultPreviewFrameSize,
  type PreviewFrameSize,
} from "~/components/ResourcePreview/previewFrame"
import {
  ReferencePreviewList,
  ResourceLibraryModal,
  WhiteBgImageModal,
  Text,
} from "~/components"
import { useWhiteBgImageStore } from "~/store/whiteBgImage"
import { VideoClipControls, type VideoClipOptions } from "./VideoClipControls"
import { useResourceUploadActivity } from "./UploadScope"
import {
  notifyUploadFailures,
  notifyUploadSuccess,
} from "~/common/media/uploadFeedback"
import styles from "./index.css"

type ResourceUploadKind = "image" | "video" | "audio"

export type ResourceUrlsChangeReason =
  | "add"
  | "auto-resolve"
  | "remove"
  | "reorder"
  | "replace"
  | "select"

export type ResourceUploadFieldRef = {
  open: () => void
}

type ResourceUploadFieldProps = {
  ref?: Ref<ResourceUploadFieldRef>
  hideUploadEntry?: boolean
  previewSize?: PreviewFrameSize
  previewLabels?: Record<string, string>
  videoPreviewRange?: { start: number; end: number }
  videoClip?: VideoClipOptions
  autoResolveVideoUrl?: string
  className?: string
  label?: ReactNode
  value: string
  onChange?: (value: string) => void
  loading?: boolean
  resourceType: ResourceUploadKind
  required?: boolean
  multiple?: boolean
  description?: string
  uploadIcon?: ReactNode
  requirementsText?: string
  showWhiteBgAction?: boolean
  subtitle?: ReactNode
  maxCount?: number
  rejectOverflow?: boolean
  /** Maximum file size in MB. */
  maxFileSize?: number
  /** Compression threshold and target in MB; defaults to 50. */
  videoTargetSizeMegabytes?: number
  enforceVideoDurationLimit?: boolean
  showVideoPreview?: boolean
  sortable?: boolean
  onUrlsChange?: (
    previousUrls: string[],
    nextUrls: string[],
    reason: ResourceUrlsChangeReason,
  ) => void
}

type ResourceUploadHeaderProps = {
  label?: ReactNode
  required: boolean
  subtitle?: ReactNode
}

const appendUrls = (value: string, urls: string[]) => {
  return Array.from(new Set([...linesToList(value), ...urls])).join("\n")
}

const limitUrls = (urls: string[], maxCount?: number) => {
  if (!maxCount || maxCount <= 0) return urls

  return urls.slice(0, maxCount)
}

const BYTES_PER_MEGABYTE = 1024 * 1024

const defaultCopy = {
  image: {
    requirements: `${IMAGE_UPLOAD_REQUIREMENTS}，请勿上传真人肖像素材`,
    placeholder: "粘贴图片链接，回车添加",
    uploadButtonText: "上传图片",
    modalTitle: "选择图片资源",
  },
  video: {
    requirements: VIDEO_UPLOAD_REQUIREMENTS,
    placeholder: "粘贴抖音视频链接，回车自动获取视频文件",
    uploadButtonText: "上传视频",
    modalTitle: "选择视频资源",
  },
  audio: {
    requirements: `音频 MP3/MPEG/WAV 不超过 ${uploadResourceLimits.audio.sizeText}`,
    placeholder: "",
    uploadButtonText: "上传音频",
    modalTitle: "选择音频资源",
  },
} as const

const ResourceUploadHeader = ({
  label,
  required,
  subtitle,
}: ResourceUploadHeaderProps) => {
  const labelNode =
    typeof label === "string" ? (
      <Flex
        align="center"
        gap={4}
        style={{ fontSize: 20 }}
      >
        <UploadIcon />
        <Text size="l">{label}</Text>
      </Flex>
    ) : (
      label
    )

  return (
    <Flex
      gap={4}
      vertical
    >
      {label && (
        <Flex
          gap={4}
          style={{ marginBottom: 8 }}
          align="center"
        >
          {labelNode}
          {required ? (
            <Text
              size="s"
              className={styles.required}
            >
              必填
            </Text>
          ) : null}
        </Flex>
      )}
      {subtitle ? <Text type="secondary">{subtitle}</Text> : null}
    </Flex>
  )
}

export const ResourceUploadField = ({
  ref,
  hideUploadEntry = false,
  previewSize,
  previewLabels,
  videoPreviewRange,
  videoClip,
  requirementsText,
  showWhiteBgAction = true,
  autoResolveVideoUrl,
  className,
  label,
  value,
  onChange,
  loading = false,
  resourceType,
  required = false,
  multiple = false,
  description,
  uploadIcon,
  subtitle,
  maxCount,
  rejectOverflow = false,
  maxFileSize,
  videoTargetSizeMegabytes,
  enforceVideoDurationLimit = true,
  showVideoPreview = true,
  sortable = false,
  onUrlsChange,
}: ResourceUploadFieldProps) => {
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const [inputUrl, setInputUrl] = useState("")
  const [resourcesByUrl, setResourcesByUrl] = useState<
    Record<string, UploadedResource>
  >({})
  const [uploadingCount, setUploadingCount] = useState(0)
  const [whiteBgSourceUrl, setWhiteBgSourceUrl] = useState("")
  const autoResolvedVideoUrlRef = useRef("")
  const valueRef = useRef(value)
  const whiteBgImageModalOpen = useWhiteBgImageStore((state) => state.open)
  const openWhiteBgImageModal = useWhiteBgImageStore((state) => state.openModal)
  const videoUpload = useVideoUpload()
  const urls = useMemo(() => linesToList(value), [value])
  const clip =
    resourceType === "video" && !multiple && urls.length === 1
      ? videoClip
      : undefined
  const previewRange = clip
    ? clip.end_time > clip.start_time
      ? { start: clip.start_time, end: clip.end_time }
      : undefined
    : videoPreviewRange
  const copy = defaultCopy[resourceType]
  const uploadRequest = useRef<AbortController | null>(null)
  useEffect(() => () => uploadRequest.current?.abort(), [])
  const isUploading = uploadingCount > 0 || videoUpload.isBusy
  const isBusy = isUploading || loading
  useResourceUploadActivity(isBusy)
  const effectiveMaxCount = multiple ? maxCount : 1
  const uploadLimit = uploadResourceLimits[resourceType]
  const effectiveMaxFileSizeInMegabytes =
    maxFileSize ?? uploadLimit.maxSize / BYTES_PER_MEGABYTE
  const effectiveMaxFileSizeInBytes =
    effectiveMaxFileSizeInMegabytes * BYTES_PER_MEGABYTE
  const effectiveMaxFileSizeText = formatFileSize(effectiveMaxFileSizeInBytes)
  const status = videoUpload.isBusy
    ? videoUpload.status
    : uploadingCount > 1
      ? `上传中...（${uploadingCount} 个）`
      : loading
        ? "处理中..."
        : "上传中..."
  const title =
    resourceType === "image"
      ? "粘贴 / 拖拽 / 点击上传图片"
      : `拖拽或点击${copy.uploadButtonText}`
  const requirements =
    resourceType === "video" && requirementsText === undefined
      ? getVideoUploadRequirements(videoTargetSizeMegabytes)
      : (requirementsText ?? copy.requirements).replace(
          uploadLimit.sizeText,
          effectiveMaxFileSizeText,
        )
  const canUploadMore = !maxCount || urls.length < maxCount
  const shouldHideSingleUploadEntry =
    hideUploadEntry || (effectiveMaxCount === 1 && urls.length >= 1)

  const updateValue = (nextValue: string, reason: ResourceUrlsChangeReason) => {
    if (!mounted.current) return
    const previousUrls = linesToList(valueRef.current)
    const nextUrls = linesToList(nextValue)

    valueRef.current = nextValue
    onChange?.(nextValue)
    onUrlsChange?.(previousUrls, nextUrls, reason)
  }

  const showMaxCountWarning = () => {
    if (!effectiveMaxCount) return

    void message.warning(
      `最多只能添加 ${effectiveMaxCount} ${resourceType === "image" ? "张图片" : `个${uploadLimit.label}`}`,
    )
  }

  const open = () => {
    if (isBusy) return
    if (!canUploadMore) {
      showMaxCountWarning()
      return
    }
    setIsAssetModalOpen(true)
  }
  useImperativeHandle(ref, () => ({ open }))

  const updateUrls = (nextUrls: string[]) => {
    const limitedUrls = limitUrls(nextUrls, effectiveMaxCount)

    if (effectiveMaxCount && nextUrls.length > limitedUrls.length) {
      showMaxCountWarning()
      if (rejectOverflow) return
    }

    updateValue(
      multiple ? appendUrls("", limitedUrls) : limitedUrls[0] || "",
      "select",
    )
  }

  const reorderUrls = (nextUrls: string[]) => {
    updateValue(nextUrls.join("\n"), "reorder")
  }

  const addUploadedResource = (
    resource: UploadedResource,
    reason: ResourceUrlsChangeReason = "add",
  ) => {
    const nextUrls = multiple
      ? linesToList(appendUrls(valueRef.current, [resource.url]))
      : [resource.url]
    const limitedUrls = limitUrls(nextUrls, effectiveMaxCount)

    if (effectiveMaxCount && nextUrls.length > limitedUrls.length) {
      showMaxCountWarning()
      return
    }

    setResourcesByUrl((current) => ({
      ...current,
      [resource.url]: resource,
    }))
    updateValue(multiple ? limitedUrls.join("\n") : resource.url, reason)
  }

  const removeUrl = (url: string) => {
    updateValue(urls.filter((item) => item !== url).join("\n"), "remove")
  }

  const openWhiteBgImage = (url: string) => {
    setWhiteBgSourceUrl(url)
    openWhiteBgImageModal(url)
  }

  const replaceWhiteBgImage = (url: string) => {
    const nextUrls = urls.map((item) =>
      item === whiteBgSourceUrl ? url : item,
    )

    if (!nextUrls.includes(url) && !urls.includes(whiteBgSourceUrl)) {
      nextUrls.push(url)
    }

    const limitedUrls = limitUrls(nextUrls, effectiveMaxCount)

    if (effectiveMaxCount && nextUrls.length > limitedUrls.length) {
      showMaxCountWarning()
      return
    }

    updateValue(multiple ? limitedUrls.join("\n") : url, "replace")
    setWhiteBgSourceUrl("")
  }

  useEffect(() => {
    if (isUploading) return

    valueRef.current = value
  }, [isUploading, value])

  useEffect(() => {
    if (whiteBgImageModalOpen) return

    setWhiteBgSourceUrl("")
  }, [whiteBgImageModalOpen])

  const handleAssetConfirm = (resources: UploadedResource[]) => {
    if (
      resourceType === "audio" &&
      resources.some(
        (resource) => getUploadedResourceKind(resource) !== "audio",
      )
    ) {
      void message.error("只能选择音频资源")
      return
    }
    if (
      resourceType === "video" &&
      resources.some((resource) => !isValidUploadedVideoResource(resource))
    ) {
      void message.error("只能选择 MP4 格式且不超过 100MB 的视频资源")
      return
    }

    if (
      resources.some(
        (resource) =>
          typeof resource.size === "number" &&
          resource.size > effectiveMaxFileSizeInBytes,
      )
    ) {
      void message.error(
        `${uploadLimit.label}大小不能超过 ${effectiveMaxFileSizeText}`,
      )
      return
    }

    const selectedUrls = resources.map((resource) => resource.url)
    setResourcesByUrl((current) => ({
      ...current,
      ...Object.fromEntries(
        resources.map((resource) => [resource.url, resource] as const),
      ),
    }))
    updateUrls(selectedUrls)
    setIsAssetModalOpen(false)
  }

  const uploadFiles = async (files: LocalUploadFile[]) => {
    if (resourceType === "video") {
      const { failures, resources } = await videoUpload.uploadVideos(
        files.map(({ file }) => file),
        {
          enforceDurationLimit: enforceVideoDurationLimit,
          maxFileSize: effectiveMaxFileSizeInBytes,
          videoTargetSizeMegabytes,
        },
      )

      resources.forEach((resource) => addUploadedResource(resource))
      notifyUploadSuccess(resources.length)
      notifyUploadFailures(failures)
      return
    }

    const controller = new AbortController()
    uploadRequest.current = controller
    setUploadingCount(files.length)

    try {
      const { failures, resources } = await uploadLocalResources({
        files,
        signal: controller.signal,
      })

      if (controller.signal.aborted) return
      resources.forEach((resource) => addUploadedResource(resource))
      notifyUploadSuccess(resources.length)
      notifyUploadFailures(failures)
    } finally {
      if (!controller.signal.aborted) setUploadingCount(0)
      if (uploadRequest.current === controller) uploadRequest.current = null
    }
  }

  const queueFiles = (files: File[]) => {
    if (isBusy || !files.length) return false

    const remainingCount = effectiveMaxCount
      ? Math.max(0, effectiveMaxCount - urls.length)
      : files.length

    if (remainingCount <= 0) {
      showMaxCountWarning()
      return false
    }

    if (rejectOverflow && files.length > remainingCount) {
      showMaxCountWarning()
      return false
    }

    const limitedFiles = files.slice(0, remainingCount)
    const { acceptedFiles, rejectedFiles, validationErrors } =
      getUploadableLocalFiles({
        allowedKinds: [resourceType],
        files: limitedFiles,
      })
    const uploadableFiles = acceptedFiles.filter(
      ({ file }) => file.size <= effectiveMaxFileSizeInBytes,
    )

    if (files.length > limitedFiles.length) {
      showMaxCountWarning()
    }

    if (rejectedFiles.length) {
      void message.error(
        `${uploadLimit.label}格式仅支持 ${uploadLimit.extensions.join(" / ")}`,
      )
    }

    if (validationErrors.length) {
      void message.error(validationErrors[0])
    }

    if (uploadableFiles.length < acceptedFiles.length) {
      void message.error(
        `${uploadLimit.label}大小不能超过 ${effectiveMaxFileSizeText}`,
      )
    }

    if (!uploadableFiles.length) return false

    void uploadFiles(uploadableFiles)

    return true
  }

  const resolveDouyinUrl = async (
    rawUrl: string,
    reason: ResourceUrlsChangeReason = "add",
  ) => {
    const nextInputUrl = rawUrl.trim()

    if (!nextInputUrl) {
      void message.warning("请输入抖音视频链接")
      return
    }

    if (!canUploadMore) {
      showMaxCountWarning()
      return
    }

    try {
      const resource = await videoUpload.uploadDouyinVideo(nextInputUrl, {
        enforceDurationLimit: enforceVideoDurationLimit,
        maxFileSize: effectiveMaxFileSizeInBytes,
        videoTargetSizeMegabytes,
      })
      addUploadedResource(resource, reason)
      setInputUrl("")
      void message.success("已上传素材")
    } catch (error) {
      if (isAbortError(error) || error instanceof ApiRequestError) {
        return
      }

      void message.error(
        error instanceof Error ? error.message : "视频处理失败",
      )
    }
  }

  const resolveAutoVideoUrl = useEffectEvent((url: string) => {
    void resolveDouyinUrl(url, "auto-resolve")
  })

  useEffect(() => {
    const nextVideoUrl = autoResolveVideoUrl?.trim()

    if (
      resourceType !== "video" ||
      !nextVideoUrl ||
      autoResolvedVideoUrlRef.current === nextVideoUrl
    ) {
      return
    }

    autoResolvedVideoUrlRef.current = nextVideoUrl
    setInputUrl(nextVideoUrl)
    resolveAutoVideoUrl(nextVideoUrl)
  }, [autoResolveVideoUrl, resourceType])
  const submitDouyinUrl = () => {
    if (videoUpload.isBusy) return

    const nextInputUrl = inputUrl.trim()

    if (!nextInputUrl) {
      void message.warning("请输入抖音视频链接")
      return
    }

    void resolveDouyinUrl(nextInputUrl)
  }

  return (
    <>
      <Flex
        className={className}
        orientation="vertical"
        gap={8}
      >
        {label || subtitle ? (
          <ResourceUploadHeader
            label={label}
            required={required}
            subtitle={subtitle}
          />
        ) : null}

        {resourceType === "video" && !shouldHideSingleUploadEntry ? (
          <Flex
            align="center"
            gap={4}
            wrap
          >
            <Input
              className={styles.urlInput}
              allowClear
              disabled={isBusy}
              onChange={(event) => setInputUrl(event.target.value)}
              onPressEnter={submitDouyinUrl}
              placeholder={copy.placeholder}
              value={inputUrl}
              suffix={
                <Button
                  disabled={isUploading}
                  loading={videoUpload.isBusy}
                  onClick={submitDouyinUrl}
                  variant="text"
                  color="primary"
                >
                  获取视频
                </Button>
              }
            />
          </Flex>
        ) : null}

        {!shouldHideSingleUploadEntry ? (
          <>
            {description ? <Text type="secondary">{description}</Text> : null}

            <Uploader
              accept={uploadLimit.accept}
              multiple={multiple}
              allowRemoteImage={resourceType === "image"}
              className={styles.uploadArea}
              description={isBusy ? status : requirements}
              icon={uploadIcon ?? <UploadIcon className={styles.uploadIcon} />}
              loading={isBusy}
              title={title}
              onFiles={queueFiles}
              onClick={open}
            />
          </>
        ) : null}

        <ReferencePreviewList
          references={urls.map((url) => ({
            ...resourcesByUrl[url],
            url,
            label: previewLabels?.[url],
            type: resourcesByUrl[url]?.type || resourceType,
            uploadedAt: resourcesByUrl[url]?.uploadedAt ?? 0,
          }))}
          size={previewSize ?? defaultPreviewFrameSize}
          videoPreviewRange={previewRange}
          videoDisplay={
            !showVideoPreview
              ? "metadata"
              : previewSize === "small"
                ? "thumbnail"
                : "details"
          }
          sortable={
            sortable &&
            multiple &&
            resourceType === "image" &&
            !isBusy &&
            urls.length > 1
          }
          onWhiteBgImage={showWhiteBgAction ? openWhiteBgImage : undefined}
          onRemove={removeUrl}
          onReorder={(resources) =>
            reorderUrls(resources.map(({ url }) => url))
          }
        />
        {clip ? (
          <VideoClipControls
            {...clip}
            videoUrl={urls[0]}
          />
        ) : null}
      </Flex>

      <ResourceLibraryModal
        videoTargetSizeMegabytes={videoTargetSizeMegabytes}
        open={isAssetModalOpen}
        title={copy.modalTitle}
        resourceType={resourceType}
        selectionMode={multiple ? "multiple" : "single"}
        selectedUrls={urls}
        maxCount={effectiveMaxCount}
        onConfirm={handleAssetConfirm}
        onCancel={() => setIsAssetModalOpen(false)}
      />
      {resourceType === "image" && whiteBgSourceUrl ? (
        <WhiteBgImageModal onConfirm={replaceWhiteBgImage} />
      ) : null}
    </>
  )
}
